import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Search, ShoppingBag, LogOut, User, LayoutDashboard, Package,
  X, WifiOff, Flame, ChevronLeft, FileDown, ClipboardList,
  Users, ShoppingCart, Warehouse, Menu, Plus, AlertTriangle, Send,
  KeyRound,
} from "lucide-react";
import offlineStorage from "@/utils/offlineStorage";
import { loadCart, saveCart, cartLineCount } from "@/utils/clientCart";
import { ThemeToggle } from "@/components/ThemeToggle";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { useClientDestination } from "@/utils/clientDestination";
import { DestinationSelector } from "@/components/DestinationSelector";
import ConnectionStatus from "@/components/ConnectionStatus";
import { ImageLightbox } from "@/components/catalog/ImageLightbox";
import { PriceDisplay, RoleBadge } from "@/components/catalog/PriceDisplay";
import { ProductCard } from "@/components/catalog/ProductCard";
import { PromoSection } from "@/components/catalog/PromoSection";
import ClientRecurringProducts from "@/pages/client/ClientRecurringProducts";
import ClientStatement from "@/pages/client/ClientStatement";

const categoryIconMap = {
  "Prodotti Triolo": "/icons/triolo.png",
  "Salumi": "/icons/salumi.png",
  "Formaggi": "/icons/formaggi.png",
  "Olive, Sottoli, Pesti": "/icons/olive.png",
  "Prodotti Ittici": "/icons/ittici.png",
  "Frutta Secca e Aromi": "/icons/frutta_secca.png",
  "Altri": "/icons/altri.png",
};

const macroConfig = [
  { name: "Prodotti Triolo", color: "amber" },
  { name: "Salumi", color: "red" },
  { name: "Formaggi", color: "yellow" },
  { name: "Olive, Sottoli, Pesti", color: "emerald" },
  { name: "Prodotti Ittici", color: "cyan" },
  { name: "Frutta Secca e Aromi", color: "orange" },
  { name: "Altri", color: "zinc" },
];

const colorMap = {
  amber: { bg: "bg-amber-500/10", border: "border-amber-500/20 hover:border-amber-500/50", text: "text-amber-400", icon: "text-amber-500" },
  red: { bg: "bg-red-500/10", border: "border-red-500/20 hover:border-red-500/50", text: "text-red-400", icon: "text-red-500" },
  yellow: { bg: "bg-yellow-500/10", border: "border-yellow-500/20 hover:border-yellow-500/50", text: "text-yellow-400", icon: "text-yellow-500" },
  emerald: { bg: "bg-emerald-500/10", border: "border-emerald-500/20 hover:border-emerald-500/50", text: "text-emerald-400", icon: "text-emerald-500" },
  cyan: { bg: "bg-cyan-500/10", border: "border-cyan-500/20 hover:border-cyan-500/50", text: "text-cyan-400", icon: "text-cyan-500" },
  orange: { bg: "bg-orange-500/10", border: "border-orange-500/20 hover:border-orange-500/50", text: "text-orange-400", icon: "text-orange-500" },
  zinc: { bg: "bg-zinc-500/10", border: "border-zinc-500/20 hover:border-zinc-500/50", text: "text-zinc-400", icon: "text-zinc-400" },
};

const trioloSubConfig = [
  { name: "Pasta Fresca", color: "amber" },
  { name: "Pasta Secca", color: "orange" },
  { name: "Prodotti da Forno", color: "red" },
];

export default function CatalogPage() {
  const navigate = useNavigate();
  const { user, logout, authAxios } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [clientPrices, setClientPrices] = useState({});  // product_id → resolved price
  const [lastPrices, setLastPrices] = useState({});      // product_id → {ultimo_prezzo, ultima_qta, ultima_data}
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [lastSync, setLastSync] = useState(null);
  const [isOfflineData, setIsOfflineData] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [activeMacro, setActiveMacro] = useState(null);
  const [trioloSub, setTrioloSub] = useState(null);
  const [showNonDisponibili, setShowNonDisponibili] = useState(false);
  const [cart, setCart] = useState({});
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  const isClientUser = user?.role === "client";
  const clientId = user?.client_id || null;
  // Cliente contrattualizzato (sconti_fissi): TUTTI i prezzi nascosti
  const hidePrices = isClientUser && !!user?.sconti_fissi;
  // Tab corrente per area cliente: "catalog" | "recurring" | "statement"
  const [clientView, setClientView] = useState("catalog");

  // Destination selection (clients with multiple destinations)
  const {
    destinations: clientDestinations,
    selectedDest,
    setSelectedDest,
    isLocked: isDestLocked,
    isHidden: isDestHidden,
    needsChoice: destNeedsChoice,
  } = useClientDestination(user, authAxios);

  // Hydrate cart from localStorage when user becomes a registered client
  useEffect(() => {
    if (isClientUser && clientId) setCart(loadCart(clientId));
    else setCart({});
  }, [isClientUser, clientId]);

  // Reactive sync: ricarica il carrello quando altri componenti (es. tab "I miei articoli")
  // lo modificano scrivendo su localStorage. Si ascolta sia l'evento custom 'triolo-cart-changed'
  // (stessa finestra) sia l'evento nativo 'storage' (eventuali tab/finestre fratelle).
  useEffect(() => {
    if (!isClientUser || !clientId) return;
    const refresh = () => setCart(loadCart(clientId));
    window.addEventListener("triolo-cart-changed", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("triolo-cart-changed", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, [isClientUser, clientId]);

  const cartCount = useMemo(() => cartLineCount(cart), [cart]);

  const handleCartChange = useCallback((product, nextQty) => {
    if (!clientId) {
      toast.error("Profilo non collegato a un'anagrafica cliente. Contatta l'amministratore.");
      return;
    }
    setCart((prev) => {
      const upd = { ...prev };
      if (!nextQty || nextQty <= 0) {
        delete upd[product.id];
      } else {
        const cp = clientPrices[product.id] || {};
        const lp = lastPrices[product.id] || null;
        const listino = Number(product.price_list) || 0;

        // Priority: PROMO (resolve-prices source==="promo") → LAST PAID → MANUAL/RULES (cp.price) → LISTINO
        let priceUnit = listino;
        let priceSource = "listino";
        if (cp.source === "promo" && Number(cp.price) > 0) {
          priceUnit = Number(cp.price);
          priceSource = "promo";
        } else if (lp && Number(lp.ultimo_prezzo) > 0) {
          priceUnit = Number(lp.ultimo_prezzo);
          priceSource = "last_paid";
        } else if (Number(cp.price) > 0) {
          priceUnit = Number(cp.price);
          priceSource = cp.source || "client_rule";
        }

        upd[product.id] = {
          product_id: product.id,
          product_name: product.name,
          product_sku: product.sku || "",
          unit: product.unit || "pz",
          iva_percentage: Number(product.iva_percentage) || 10,
          price_unit: priceUnit,
          price_list: listino,
          price_source: priceSource,
          quantity: nextQty,
          note: prev[product.id]?.note || "",
          sc1: 0, sc2: 0, sc3: 0, sc4: 0,
        };
      }
      saveCart(clientId, upd);
      return upd;
    });
  }, [clientId, clientPrices, lastPrices]);

  // Last prices fetch — re-runs when destination changes for clients with multiple destinations
  useEffect(() => {
    if (!isClientUser || !clientId) {
      setLastPrices({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const lp = await authAxios.get(`/clients/${clientId}/last-prices`, {
          params: selectedDest ? { dest_id: selectedDest } : {},
        });
        if (!cancelled) setLastPrices(lp.data?.prices || {});
      } catch {
        if (!cancelled) setLastPrices({});
      }
    })();
    return () => { cancelled = true; };
  }, [authAxios, isClientUser, clientId, selectedDest]);

  // Swipe gesture
  useEffect(() => {
    const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; touchStartY.current = e.touches[0].clientY; };
    const handleTouchEnd = (e) => {
      if (touchStartX.current === null) return;
      const dx = e.changedTouches[0].clientX - touchStartX.current;
      const dy = Math.abs(e.changedTouches[0].clientY - touchStartY.current);
      if (touchStartX.current < 40 && dx > 80 && dy < 100) setDrawerOpen(true);
      if (drawerOpen && dx < -80 && dy < 100) setDrawerOpen(false);
      touchStartX.current = null;
    };
    document.addEventListener("touchstart", handleTouchStart, { passive: true });
    document.addEventListener("touchend", handleTouchEnd, { passive: true });
    return () => { document.removeEventListener("touchstart", handleTouchStart); document.removeEventListener("touchend", handleTouchEnd); };
  }, [drawerOpen]);

  const fetchData = useCallback(async () => {
    try {
      if (navigator.onLine) {
        const [pRes, cRes] = await Promise.all([authAxios.get("/products"), authAxios.get("/categories")]);
        setProducts(pRes.data); setCategories(cRes.data); setIsOfflineData(false);
        await offlineStorage.saveProducts(pRes.data); await offlineStorage.saveCategories(cRes.data);
        setLastSync(new Date().toISOString());

        // Per il cliente, risolvi i suoi prezzi (promo + regole + manuale) per tutti gli articoli
        if (user?.role === "client" && user?.client_id) {
          try {
            const allIds = pRes.data.map(p => p.id);
            const r = await authAxios.post(`/clients/${user.client_id}/resolve-prices`, { product_ids: allIds });
            setClientPrices(r.data.prices || {});
          } catch (e) {
            console.error("Failed to resolve client prices", e);
          }
          // Storico prezzi pagati (zonaclidestmasc → giri_presa_ordine)
          // Re-fetched separately whenever selectedDest changes.
        }
      } else { await loadOfflineData(); }
    } catch { await loadOfflineData(); } finally { setLoading(false); }
  }, [authAxios, user]);

  useEffect(() => {
    offlineStorage.init().then(async () => { setLastSync(await offlineStorage.getLastSync()); }).catch(() => {});
    fetchData();
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => { window.removeEventListener('online', handleOnline); window.removeEventListener('offline', handleOffline); };
  }, [fetchData]);

  const loadOfflineData = async () => {
    try {
      const [op, oc] = await Promise.all([offlineStorage.getProducts(), offlineStorage.getCategories()]);
      if (op.length > 0) { setProducts(op); setCategories(oc); setIsOfflineData(true); setLastSync(await offlineStorage.getLastSync()); }
      else toast.error("Nessun dato offline disponibile");
    } catch { toast.error("Errore nel caricamento dati offline"); }
  };

  const handleSync = async () => {
    if (!navigator.onLine) { toast.error("Connessione non disponibile"); return; }
    setLoading(true); await fetchData(); toast.success("Catalogo sincronizzato");
  };

  const handleQuickPhotoUpload = async (productId, file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type)) { toast.error("Formato non supportato"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("File troppo grande (max 5MB)"); return; }
    const t = toast.loading("Caricamento foto...");
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await authAxios.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await authAxios.put(`/products/${productId}`, { image_url: r.data.url });
      setProducts(prev => prev.map(p => p.id === productId ? { ...p, image_url: r.data.url } : p));
      toast.success("Foto aggiornata!", { id: t });
    } catch (e) { toast.error(e.response?.data?.detail || "Errore caricamento", { id: t }); }
  };

  const handleLogout = () => { logout(); navigate("/login"); };

  // Grouped products
  const groupedProducts = {};
  macroConfig.forEach(m => { groupedProducts[m.name] = []; });
  products.forEach(p => { const m = p.macro_category || "Altri"; if (!groupedProducts[m]) groupedProducts[m] = []; groupedProducts[m].push(p); });

  const sortProducts = (arr) => arr.sort((a, b) => {
    const da = a.non_disponibile ? 1 : 0, db = b.non_disponibile ? 1 : 0;
    if (da !== db) return da - db;
    return (a.category_code || "").localeCompare(b.category_code || "");
  });

  const isGlobalSearch = !activeMacro && searchQuery.trim().length > 0;
  const globalSearchResults = isGlobalSearch
    ? sortProducts(products.filter(p => {
        const q = searchQuery.toLowerCase();
        return (p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q)) && (showNonDisponibili || !p.non_disponibile);
      }))
    : [];

  const activeProducts = activeMacro
    ? sortProducts((activeMacro === "__offerte__"
        ? products.filter(p => p.price_promo && p.price_promo > 0)
        : (groupedProducts[activeMacro] || [])
      ).filter(p => {
        const ms = !searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.sku.toLowerCase().includes(searchQuery.toLowerCase());
        return ms && (!trioloSub || p.triolo_sub === trioloSub) && (showNonDisponibili || !p.non_disponibile);
      }))
    : [];

  // Drawer menu items
  const drawerMenuItems = [];
  if (user.role === "admin") {
    drawerMenuItems.push({ to: "/admin", icon: LayoutDashboard, label: "Dashboard" }, { to: "/admin/orders", icon: ShoppingCart, label: "Ordini" }, { to: "/admin/quotes", icon: ClipboardList, label: "Preventivi" }, { to: "/admin/clients", icon: Users, label: "Clienti" }, { to: "/admin/exports", icon: FileDown, label: "Esportazioni PDF" });
  } else if (user.role === "agent") {
    drawerMenuItems.push({ to: "/agent", icon: LayoutDashboard, label: "Dashboard" }, { to: "/agent/orders", icon: ShoppingCart, label: "Ordini" }, { to: "/agent/quotes", icon: ClipboardList, label: "Preventivi" }, { to: "/agent/clients", icon: Users, label: "Clienti" }, { to: "/admin/exports", icon: FileDown, label: "Esportazioni PDF" });
    if (user.magazziniere_extra) {
      drawerMenuItems.push({ to: "/warehouse", icon: Warehouse, label: "Magazzino" });
    }
  } else if (user.role === "magazziniere") {
    drawerMenuItems.push({ to: "/warehouse", icon: Warehouse, label: "Dashboard" }, { to: "/warehouse/orders", icon: ShoppingCart, label: "Ordini" });
  }

  if (loading) return <div className="min-h-screen bg-[#09090b] flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500" /></div>;

  const productVariant = (m) => m === "__offerte__" ? "offerte" : m === "Prodotti Triolo" ? "triolo" : "default";

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-[60]" data-testid="catalog-drawer">
          <div className="absolute inset-0 bg-black/60" onClick={() => setDrawerOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-[#0f0f11] border-r border-white/10 shadow-2xl animate-in slide-in-from-left duration-200 flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <span className="font-heading text-lg font-bold text-white">Triolo.Store</span>
              <button onClick={() => setDrawerOpen(false)} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <nav className="flex-1 p-3 overflow-y-auto">
              <ul className="space-y-1">
                {drawerMenuItems.map((item) => (
                  <li key={item.to}><Link to={item.to} onClick={() => setDrawerOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-300 hover:text-white hover:bg-white/5 transition-colors text-sm"><item.icon className="w-4 h-4" /><span>{item.label}</span></Link></li>
                ))}
                <li><div className="flex items-center gap-3 px-4 py-3 rounded-lg text-amber-400 bg-amber-500/10 text-sm"><Package className="w-4 h-4" /><span>Catalogo</span></div></li>
              </ul>
            </nav>
            <div className="p-3 border-t border-white/5">
              <div className="flex items-center gap-2 mb-2 px-2">
                <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center shrink-0"><span className="text-purple-400 font-medium text-sm">{user?.name?.charAt(0).toUpperCase()}</span></div>
                <div className="flex-1 min-w-0"><p className="text-sm font-medium text-white truncate">{user?.name}</p><p className="text-[10px] text-zinc-500 truncate">{user?.email}</p></div>
              </div>
              <Button variant="outline" onClick={() => { setDrawerOpen(false); setChangePasswordOpen(true); }} className="w-full h-8 text-xs border-white/10 text-zinc-300 hover:text-amber-400 hover:border-amber-400/50 mb-2" data-testid="drawer-change-password-btn"><KeyRound className="w-3 h-3 mr-1" />Cambia password</Button>
              <Button variant="outline" onClick={() => { setDrawerOpen(false); handleLogout(); }} className="w-full h-8 text-xs border-white/10 text-zinc-400 hover:text-red-400 hover:border-red-400/50" data-testid="drawer-logout-btn"><LogOut className="w-3 h-3 mr-1" />Esci</Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-50 header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Mobile */}
          <div className="sm:hidden py-2 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Link to="/catalog" data-testid="go-catalog-btn" className="flex-1"><Button size="sm" variant="ghost" className="w-full text-zinc-300 hover:text-white hover:bg-white/10 h-9 text-xs border border-white/10"><ShoppingBag className="w-4 h-4 mr-1.5" />Catalogo</Button></Link>
              {(user.role === "admin" || user.role === "agent") && <Link to={user.role === "admin" ? "/admin/orders/new" : "/agent/orders/new"} data-testid="go-new-order-btn" className="flex-1"><Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium h-9 text-xs"><Plus className="w-4 h-4 mr-1.5" />Nuovo Ordine</Button></Link>}
              {(user.role === "admin" || user.role === "agent" || user.role === "magazziniere") && <Link to={user.role === "admin" ? "/admin" : user.role === "agent" ? "/agent" : "/warehouse"} data-testid="go-dashboard-btn" className="flex-1"><Button size="sm" className="w-full bg-amber-500 hover:bg-amber-600 text-black font-medium h-9 text-xs"><LayoutDashboard className="w-4 h-4 mr-1.5" />Dashboard</Button></Link>}
              {user.role === "client" && <Link to="/client/orders" data-testid="go-my-orders-btn" className="flex-1"><Button size="sm" className="w-full bg-violet-500 hover:bg-violet-600 text-white font-medium h-9 text-xs"><FileDown className="w-4 h-4 mr-1.5" />I Miei Ordini</Button></Link>}
            </div>
            <div className="flex items-center gap-2 pb-2">
              <div className="relative flex-1 search-bar rounded-lg">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input data-testid="search-input-mobile" type="text" placeholder="Cerca prodotti o SKU..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 bg-transparent border-0 focus:ring-0 text-white placeholder:text-zinc-500" />
              </div>
              <ConnectionStatus onSync={handleSync} lastSync={lastSync} />
              {isClientUser && clientId && (
                <Link
                  to="/client/cart"
                  data-testid="header-cart-btn-mobile"
                  className="relative inline-flex items-center justify-center h-9 w-9 shrink-0 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 hover:text-amber-300 transition-colors"
                  aria-label="Carrello"
                >
                  <ShoppingCart className="w-4 h-4" />
                  <span
                    data-testid="header-cart-count-mobile"
                    className={`absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full text-white text-[10px] font-bold flex items-center justify-center ${cartCount > 0 ? "bg-red-500" : "bg-zinc-600"}`}
                  >
                    {cartCount || 0}
                  </span>
                </Link>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid="user-menu-btn" className="text-zinc-400 hover:text-white h-9 w-9 shrink-0 border border-white/10"><User className="w-4 h-4" /></Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#18181b] border-white/10">
                  <DropdownMenuLabel className="text-zinc-400">{user.name} ({user.role})</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem data-testid="change-password-btn-mobile" onClick={() => setChangePasswordOpen(true)} className="text-zinc-300 cursor-pointer focus:text-amber-400"><KeyRound className="w-4 h-4 mr-2" />Cambia password</DropdownMenuItem>
                  <DropdownMenuItem data-testid="logout-btn" onClick={handleLogout} className="text-red-400 cursor-pointer"><LogOut className="w-4 h-4 mr-2" />Esci</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          {/* Desktop */}
          <div className="hidden sm:flex items-center justify-between h-16">
            <span className="font-heading text-xl font-bold text-white">Triolo.Store</span>
            <div className="flex-1 max-w-xl mx-4">
              <div className="relative search-bar rounded-lg w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input data-testid="search-input" type="text" placeholder="Cerca prodotti o SKU..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 bg-transparent border-0 focus:ring-0 text-white placeholder:text-zinc-500" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/catalog" data-testid="go-catalog-btn-desktop"><Button size="sm" variant="ghost" className="text-zinc-300 hover:text-white hover:bg-white/10 h-8 px-3 text-xs"><ShoppingBag className="w-3.5 h-3.5 mr-1.5" />Catalogo</Button></Link>
              {(user.role === "admin" || user.role === "agent") && <Link to={user.role === "admin" ? "/admin/orders/new" : "/agent/orders/new"} data-testid="go-new-order-btn-desktop"><Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium h-8 px-3 text-xs"><Plus className="w-3.5 h-3.5 mr-1.5" />Nuovo Ordine</Button></Link>}
              {(user.role === "admin" || user.role === "agent" || user.role === "magazziniere") && <Link to={user.role === "admin" ? "/admin" : user.role === "agent" ? "/agent" : "/warehouse"} data-testid="go-dashboard-btn-desktop"><Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-black font-medium h-8 px-3 text-xs"><LayoutDashboard className="w-3.5 h-3.5 mr-1.5" />Dashboard</Button></Link>}
              {user.role === "client" && <Link to="/client/orders" data-testid="go-my-orders-btn-desktop"><Button size="sm" className="bg-violet-500 hover:bg-violet-600 text-white font-medium h-8 px-3 text-xs"><FileDown className="w-3.5 h-3.5 mr-1.5" />I Miei Ordini</Button></Link>}
              <ConnectionStatus onSync={handleSync} lastSync={lastSync} />
              <ThemeToggle className="!w-8 !h-8" />
              {isClientUser && clientId && (
                <Link
                  to="/client/cart"
                  data-testid="header-cart-btn-desktop"
                  className="relative inline-flex items-center gap-2 h-8 px-3 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 hover:text-amber-300 transition-colors text-xs font-medium"
                  aria-label="Carrello"
                >
                  <ShoppingCart className="w-3.5 h-3.5" />
                  <span>Carrello</span>
                  <span
                    data-testid="header-cart-count-desktop"
                    className={`min-w-[20px] h-[18px] px-1 rounded-full text-white text-[10px] font-bold flex items-center justify-center ${cartCount > 0 ? "bg-red-500" : "bg-zinc-600"}`}
                  >
                    {cartCount || 0}
                  </span>
                </Link>
              )}
              <RoleBadge role={user.role} />
              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button variant="ghost" data-testid="user-menu-btn-desktop" className="flex items-center gap-2 text-zinc-300 hover:text-white hover:bg-white/10"><User className="w-4 h-4" /><span>{user.name}</span></Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#18181b] border-white/10">
                  <DropdownMenuLabel className="text-zinc-400">Account</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-white/10" />
                  {(user.role === "admin" || user.role === "agent") && <DropdownMenuItem asChild><Link to="/admin/exports" className="flex items-center gap-2 cursor-pointer"><FileDown className="w-4 h-4" />Esportazioni PDF</Link></DropdownMenuItem>}
                  <DropdownMenuItem data-testid="change-password-btn-desktop" onClick={() => setChangePasswordOpen(true)} className="text-zinc-300 focus:text-amber-400 cursor-pointer"><KeyRound className="w-4 h-4 mr-2" />Cambia password</DropdownMenuItem>
                  <DropdownMenuItem data-testid="logout-btn-desktop" onClick={handleLogout} className="text-red-400 focus:text-red-400 cursor-pointer"><LogOut className="w-4 h-4 mr-2" />Esci</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-8">
        {isClientUser && clientId && !isDestHidden && (
          <div className="mb-4" data-testid="catalog-dest-wrapper">
            <DestinationSelector
              destinations={clientDestinations}
              selectedDest={selectedDest}
              onChange={setSelectedDest}
              isLocked={isDestLocked}
              forceOpen={destNeedsChoice}
              mandatory
            />
          </div>
        )}
        {isOfflineData && (
          <div className="mb-6 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center gap-3">
            <WifiOff className="w-5 h-5 text-amber-500" />
            <div className="flex-1"><p className="text-amber-200 text-sm font-medium">Modalità Offline</p><p className="text-amber-200/70 text-xs">Stai visualizzando dati salvati localmente.</p></div>
          </div>
        )}

        {isClientUser && !clientId && (
          <div data-testid="client-not-linked-banner" className="mb-6 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-amber-200 text-sm font-semibold">Profilo non collegato</p>
              <p className="text-amber-200/70 text-xs mt-0.5">
                Il tuo account non è ancora associato ad un'anagrafica cliente. Per inviare ordini contatta l'amministratore — nel frattempo puoi consultare il catalogo.
              </p>
            </div>
          </div>
        )}

        {/* CLIENT TABS — visibili solo agli utenti con role=client */}
        {isClientUser && (
          <div className="mb-6 flex gap-2 overflow-x-auto pb-1" data-testid="client-tabs">
            {[
              { id: "catalog", label: "Catalogo" },
              { id: "recurring", label: "I miei articoli" },
              { id: "statement", label: "Estratto conto" },
            ].filter((t) => t.id !== "statement" || !user?.escluso_estratto_conto).map((t) => (
              <button
                key={t.id}
                onClick={() => setClientView(t.id)}
                data-testid={`client-tab-${t.id}`}
                className={`px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors ${
                  clientView === t.id
                    ? "bg-orange-500 text-black shadow-lg shadow-orange-500/20"
                    : "bg-zinc-900 text-zinc-300 hover:bg-zinc-800 border border-zinc-800"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {/* RECURRING ARTICLES VIEW (solo client) */}
        {isClientUser && clientView === "recurring" && (
          <ClientRecurringProducts clientId={clientId} destId={selectedDest} />
        )}

        {/* STATEMENT VIEW (solo client, non inibito) */}
        {isClientUser && clientView === "statement" && !user?.escluso_estratto_conto && (
          <ClientStatement />
        )}

        {/* CATALOG CONTENT (default / agenti / admin) */}
        {(!isClientUser || clientView === "catalog") && (
        <>
        {/* MACRO GRID */}
        {!activeMacro && !isGlobalSearch && (
          <div>
            <h2 className="font-heading text-xl sm:text-2xl font-bold text-white mb-6">Catalogo</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {(() => { const pp = products.filter(p => p.price_promo && p.price_promo > 0); return pp.length > 0 ? (
                <button key="__offerte__" data-testid="macro-btn-Offerte" onClick={() => setActiveMacro("__offerte__")} className="relative overflow-hidden p-6 rounded-xl border border-red-500/20 hover:border-red-500/50 bg-red-500/10 text-left transition-all duration-200 hover:scale-[1.02] cursor-pointer">
                  <Flame className="absolute -right-2 -bottom-2 w-28 h-28 text-red-500 opacity-10 pointer-events-none" />
                  <div className="relative z-10"><h3 className="font-heading text-base sm:text-lg font-bold text-white mb-1">Offerte in Corso</h3><p className="text-sm text-red-400 font-mono">{pp.length} prodotti</p></div>
                </button>) : null; })()}
              {macroConfig.map(({ name, color }) => {
                const count = (groupedProducts[name] || []).filter(p => !p.non_disponibile).length;
                if (count === 0) return null;
                const c = colorMap[color] || colorMap.zinc;
                return (
                  <button key={name} data-testid={`macro-btn-${name}`} onClick={() => setActiveMacro(name)} className={`relative overflow-hidden p-6 rounded-xl border ${c.border} ${c.bg} text-left transition-all duration-200 hover:scale-[1.02] cursor-pointer`}>
                    <img src={categoryIconMap[name]} alt="" className="absolute -right-2 -bottom-2 w-28 h-28 object-contain opacity-30 pointer-events-none" />
                    <div className="relative z-10"><h3 className="font-heading text-base sm:text-lg font-bold text-white mb-1">{name}</h3><p className={`text-sm ${c.text} font-mono`}>{count} prodotti</p></div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* GLOBAL SEARCH */}
        {isGlobalSearch && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div><h2 className="font-heading text-xl sm:text-2xl font-bold text-white">Risultati ricerca</h2><p className="text-xs text-zinc-500">{globalSearchResults.length} prodotti trovati per "{searchQuery}"</p></div>
              <button data-testid="clear-search-btn" onClick={() => setSearchQuery("")} className="text-xs text-amber-400 hover:text-amber-300 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">Cancella ricerca</button>
            </div>
            {globalSearchResults.length === 0 ? (
              <div className="text-center py-16"><Package className="w-16 h-16 text-zinc-600 mx-auto mb-4" /><h3 className="text-xl font-medium text-zinc-400 mb-2">Nessun prodotto trovato</h3></div>
            ) : (
              <div className="product-grid">{globalSearchResults.map(p => <ProductCard key={p.id} product={p} userRole={user.role} clientPrice={clientPrices[p.id]} lastPrice={lastPrices[p.id]} onImageClick={setLightboxImage} onPhotoUpload={handleQuickPhotoUpload} cartQty={cart[p.id]?.quantity || 0} onCartChange={isClientUser ? handleCartChange : null} />)}</div>
            )}
          </div>
        )}

        {/* PRODUCT LIST */}
        {activeMacro && (
          <div>
            <div className="sticky top-[88px] sm:top-16 z-40 bg-[#09090b] py-3 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
              <div className="flex items-center gap-4">
                <button data-testid="back-to-catalog-btn" onClick={() => { setActiveMacro(null); setSearchQuery(""); setTrioloSub(null); }} className="p-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-amber-500/40 text-zinc-400 hover:text-white transition-colors active:scale-95"><ChevronLeft className="w-5 h-5" /></button>
                <div className="flex-1 min-w-0"><h2 className="font-heading text-lg sm:text-2xl font-bold text-white truncate">{activeMacro === "__offerte__" ? "Offerte in Corso" : activeMacro}</h2><p className="text-xs text-zinc-500">{activeProducts.length} prodotti</p></div>
                <label className="flex items-center gap-2 cursor-pointer select-none shrink-0" data-testid="show-non-disponibili-toggle">
                  <input type="checkbox" checked={showNonDisponibili} onChange={(e) => setShowNonDisponibili(e.target.checked)} className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-amber-500 focus:ring-amber-500/30" />
                  <span className="text-xs text-zinc-400">Mostra non disponibili</span>
                </label>
              </div>
            </div>
            {/* Triolo Sub-categories */}
            {activeMacro === "Prodotti Triolo" && !trioloSub && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {trioloSubConfig.map(({ name, color }) => {
                  const count = (groupedProducts["Prodotti Triolo"] || []).filter(p => p.triolo_sub === name && !p.non_disponibile).length;
                  if (count === 0) return null;
                  const c = colorMap[color];
                  return (<button key={name} data-testid={`triolo-sub-btn-${name}`} onClick={() => setTrioloSub(name)} className={`p-6 rounded-xl border ${c.border} ${c.bg} text-left transition-all duration-200 hover:scale-[1.02] cursor-pointer`}><Package className={`w-8 h-8 ${c.icon} mb-3`} /><h3 className="font-heading text-base sm:text-lg font-bold text-white mb-1">{name}</h3><p className={`text-sm ${c.text} font-mono`}>{count} prodotti</p></button>);
                })}
              </div>
            )}
            {(activeMacro !== "Prodotti Triolo" || trioloSub) && (
              <>
                {activeMacro === "Prodotti Triolo" && trioloSub && (
                  <div className="flex items-center gap-3 mb-6">
                    <button data-testid="back-to-triolo-btn" onClick={() => setTrioloSub(null)} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-white transition-colors"><ChevronLeft className="w-5 h-5" /></button>
                    <div><h3 className="font-heading text-lg font-semibold text-white">{trioloSub}</h3><p className="text-xs text-zinc-500">{activeProducts.length} prodotti</p></div>
                  </div>
                )}
                {activeProducts.length === 0 ? (
                  <div className="text-center py-16"><Package className="w-16 h-16 text-zinc-600 mx-auto mb-4" /><h3 className="text-xl font-medium text-zinc-400 mb-2">Nessun prodotto trovato</h3></div>
                ) : (
                  <div className="product-grid">{activeProducts.map(p => <ProductCard key={p.id} product={p} userRole={user.role} clientPrice={clientPrices[p.id]} lastPrice={lastPrices[p.id]} hidePrice={hidePrices} variant={productVariant(activeMacro)} onImageClick={setLightboxImage} onPhotoUpload={handleQuickPhotoUpload} cartQty={cart[p.id]?.quantity || 0} onCartChange={isClientUser ? handleCartChange : null} />)}</div>
                )}
              </>
            )}
          </div>
        )}
        </>
        )}

        {lightboxImage && <ImageLightbox src={lightboxImage.src} alt={lightboxImage.alt} onClose={() => setLightboxImage(null)} />}
      </main>
      <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
    </div>
  );
}
